import { useSessionKey } from "@auth/sessionStore";
import { dvmNames } from "@board/groupBoardVisits";
import { boardName } from "@board/VisitCard";
import { captureClient } from "@capture/captureClient";
import { captureKeys } from "@capture/captureQueries";
import { joinDots } from "@displays/examRoomFormat";
import { usePatientSprites } from "@patients/signalment";
import { RoomTileConfirm } from "@rooms/RoomConfirmControl";
import type { Room } from "@rooms/roomsClient";
import { useRoomsList } from "@rooms/roomsQueries";
import {
  cn,
  EmptyNote,
  LabelTape,
  PatientSprites,
  Skeleton,
  TimeLabel,
} from "@scribenote/brian-design-system";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { groupBy } from "@utils/group-by";
import {
  isConfirmedIn,
  isVisitCancelled,
  type TodayVisit,
} from "@visits/visitsClient";
import { useBoardVisits } from "@visits/visitsQueries";

const BAND =
  "shrink-0 rounded-[4px] border border-rule bg-card px-5 py-4 shadow-sheet";
const GRID = "grid grid-cols-[repeat(auto-fit,minmax(12rem,1fr))] gap-3";
const TILE =
  "flex min-w-0 flex-col gap-2 rounded-[6px] border border-rule px-4 py-3.5";

// Keyed on who is in which room, so a move re-reads the stays instead of waiting for the poll.
const useRoomEntries = (occupancy: string) => {
  const sessionKey = useSessionKey();
  return useQuery({
    queryKey: [...captureKeys.all, sessionKey, "front-desk-stays", occupancy],
    queryFn: () => captureClient.timeline(),
    select: (timeline) => {
      const entered = new Map<string, string>();
      for (const room of timeline.rooms)
        for (const stay of room.stays)
          if (stay.leftAt === null)
            entered.set(`${stay.visitUuid}:${room.roomUuid}`, stay.enteredAt);
      return entered;
    },
    placeholderData: keepPreviousData,
    refetchInterval: 60_000,
  });
};

const byRoomName = (a: Room, b: Room) =>
  a.name.localeCompare(b.name, undefined, { numeric: true });

export const RoomsBand = () => {
  const sessionKey = useSessionKey();
  const { data: rooms } = useRoomsList(sessionKey, true);
  const present = useBoardVisits(sessionKey).filter(
    (v) => v.currentRoomUuid !== null && !isVisitCancelled(v),
  );
  const occupancy = present
    .map((v) => `${v.id}:${v.currentRoomUuid}`)
    .sort()
    .join(",");
  const { data: entered } = useRoomEntries(occupancy);

  if (rooms.length === 0)
    return (
      <section aria-label="Rooms" className={BAND}>
        <EmptyNote className="p-4">No rooms set up.</EmptyNote>
      </section>
    );

  return (
    <section aria-label="Rooms" className={BAND}>
      <ul className={GRID}>
        {[...rooms].sort(byRoomName).map((room) => {
          const visits = present.filter((v) => v.currentRoomUuid === room.id);
          const enteredAt = (v: TodayVisit) =>
            entered?.get(`${v.id}:${room.id}`) ?? "";
          const latest = visits.sort((a, b) =>
            enteredAt(b).localeCompare(enteredAt(a)),
          )[0];
          return (
            <li key={room.id} className="min-w-0">
              {latest ? (
                <OccupiedTile
                  room={room}
                  visits={visits}
                  since={enteredAt(latest) || null}
                />
              ) : (
                <FreeTile room={room} />
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
};

const TileHead = ({ room, since }: { room: Room; since: string | null }) => (
  <span className="flex items-center justify-between gap-2">
    <LabelTape className="min-w-0 truncate">{room.name}</LabelTape>
    {since && (
      <span className="flex shrink-0 items-center gap-1 font-mono text-xs text-annotation">
        since
        <TimeLabel value={since} className="text-xs" />
      </span>
    )}
  </span>
);

const SHOWN_ROWS = 4;

type RowSize = "lead" | "pack";

/** One pet, or a household's pets in the room under the household label (BRI-382). */
const Row = ({ visits, size }: { visits: TodayVisit[]; size: RowSize }) => {
  const sprites = usePatientSprites(visits.map((v) => v.patientId));
  const [first] = visits;
  const household = visits.length > 1;
  const detail = household
    ? joinDots(
        visits.map((v) => v.patientName).join(", "),
        ...dvmNames(visits.map((v) => v.assignedUserName)),
      )
    : joinDots(first.assignedUserName, first.reason ?? first.visitType);
  return (
    <span className="flex min-w-0 items-center gap-2.5">
      <PatientSprites sprites={sprites} size={size === "lead" ? 44 : 28} />
      <span className="flex min-w-0 flex-col">
        <span
          className={cn(
            "truncate font-display font-medium tracking-[-0.01em] text-ink",
            size === "lead" ? "text-xl" : "text-base leading-tight",
          )}
        >
          {household ? first.householdLabel : first.patientName}
        </span>
        <span className="truncate text-[0.8125rem] text-annotation">
          {detail}
        </span>
      </span>
    </span>
  );
};

/**
 * `visits` is newest-entered first. A household is one row with its sprites
 * stacked; the tile shows up to four rows, then counts the pets past them.
 */
const OccupiedTile = ({
  room,
  visits,
  since,
}: {
  room: Room;
  visits: TodayVisit[];
  since: string | null;
}) => {
  const rows = groupBy(visits, (v) => v.householdId);
  const single = rows.length === 1;
  const shown = rows.slice(0, SHOWN_ROWS);
  const more = visits.length - shown.flat().length;
  const householdLead = visits.find((v) => !isConfirmedIn(v, room.id));

  return (
    <div className={cn(TILE, "bg-card")}>
      <TileHead room={room} since={since} />
      {shown.map((row) => (
        <Row key={row[0].id} visits={row} size={single ? "lead" : "pack"} />
      ))}
      {more > 0 && (
        <span className="truncate text-[0.8125rem] text-annotation">
          +{more} more
        </span>
      )}
      {visits.map((v) => (
        <RoomTileConfirm
          key={v.id}
          visit={v}
          name={boardName(v)}
          roomName={room.name}
          label={visits.length === 1 ? undefined : v.patientName}
          offerHousehold={v === householdLead}
        />
      ))}
    </div>
  );
};

const FreeTile = ({ room }: { room: Room }) => (
  <div className={cn(TILE, "bg-tint-recorded")}>
    <TileHead room={room} since={null} />
    <span className="flex min-w-0 flex-col">
      <span className="font-display text-xl font-medium tracking-[-0.01em] text-ink">
        Free
      </span>
      <span className="truncate text-[0.8125rem] text-annotation">
        Ready for a walk-in
      </span>
    </span>
  </div>
);

export const RoomsBandSkeleton = () => (
  <div aria-busy="true" className={BAND}>
    <div className={GRID}>
      {[0, 1, 2, 3, 4].map((i) => (
        <Skeleton key={i} index={i} className="h-[5.5rem] rounded-[6px]" />
      ))}
    </div>
  </div>
);
